import KpiCard from "./KpiCard";
import InsightPanel from "../Insights/InsightPanel";

function StatTile({ label, value }) {
  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-xl px-3 py-2.5">
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function AutoDashboard({ session, onAskQuestion }) {

  if (!session?.dataset?.length) return null;

  const dashboard = session?.dashboard;

  if (!dashboard) return null;

  const { overview, dataQuality } = dashboard;

  return (
    <div className="p-6 space-y-8">

      {/* KPI GRID */}
      <section>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Key Performance Indicators
        </h2>

        <div className="w-full min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {dashboard.kpis.map((kpi, index) => (
            <KpiCard
              key={index}
              title={kpi.title}
              value={kpi.value}
              subtext={kpi.subtext}
            />
          ))}
        </div>
      </section>

      {/* DATASET OVERVIEW */}
      {overview && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Dataset Overview
          </h2>

          <div className="grid grid-cols-3 gap-2.5">
            <StatTile label="Records" value={overview.records.toLocaleString()} />
            <StatTile label="Columns" value={overview.columns} />
            <StatTile label="Numeric" value={overview.numericColumns} />
            <StatTile label="Categorical" value={overview.categoricalColumns} />
            <StatTile label="Date" value={overview.dateColumns} />
          </div>
        </section>
      )}

      {/* DATA QUALITY */}
      {dataQuality && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Data Quality
          </h2>

          {dataQuality.isClean ? (
            <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-950/30 border border-emerald-900/60 rounded-xl px-4 py-3">
              <span>✓</span> No data quality issues detected.
            </div>
          ) : (
            <div className="space-y-2">

              {dataQuality.missingValueColumns.slice(0, 4).map((c, i) => (
                <div key={`missing-${i}`} className="text-xs bg-amber-950/30 border border-amber-900/50 rounded-lg px-3 py-2 text-amber-300">
                  <span className="font-medium">{c.column}</span> is missing in {c.missingPercent.toFixed(1)}% of records.
                </div>
              ))}

              {dataQuality.duplicateCount > 0 && (
                <div className="text-xs bg-amber-950/30 border border-amber-900/50 rounded-lg px-3 py-2 text-amber-300">
                  {dataQuality.duplicateCount.toLocaleString()} duplicate record{dataQuality.duplicateCount === 1 ? "" : "s"} detected.
                </div>
              )}

              {dataQuality.typeWarnings.map((w, i) => (
                <div key={`type-${i}`} className="text-xs bg-amber-950/30 border border-amber-900/50 rounded-lg px-3 py-2 text-amber-300">
                  <span className="font-medium">{w.column}</span>: {w.issue}.
                </div>
              ))}

            </div>
          )}
        </section>
      )}

      {/* KEY INSIGHTS */}
      <section>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Key Insights
        </h2>

        <InsightPanel insights={session.insights} />
      </section>

      {/* RECOMMENDED ANALYSIS */}
      {session.suggestedQuestions?.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Recommended Analysis
          </h2>

          <div className="flex flex-wrap gap-2">
            {session.suggestedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => onAskQuestion?.(q)}
                className="text-xs bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-blue-500/60 transition px-3 py-2 rounded-full text-gray-300 text-left"
              >
                {q}
              </button>
            ))}
          </div>

          <p className="text-[11px] text-gray-600 mt-2">
            Click a question to ask it in chat.
          </p>
        </section>
      )}

    </div>
  );
}
